import * as React from 'react';

import {StatusBar} from 'shells/control-bar/status-bar';
import {ErrorState} from 'shells/error-state/error-state';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {getImportStatusBadge} from 'shells/import2/helpers';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {RelativeTimestamp} from 'shells/timestamp/relative-timestamp';

import type {CrmOptionType} from 'nutshell-graphql-types';

import {PageContainer} from '../../../layout/page-container';

import {analyticsClient} from '../../../../analytics-client';
import {appHistory} from '../../../../history';

import {SettingsSidebar} from '../../settings-sidebar';

import {useGetImport2ImportStatus} from './graphql/hooks';
import {useInitiateImport} from './graphql/hooks/use-initiate-import2-import';
import {ImportStatus} from './import-status';

import styles from './import2-status-page.module.css';

type Props = {
    originId: string | null | undefined;
};

export function Import2StatusPage(props: Props) {
    const {importStatus, isLoading, errorMessage, refetch, startPolling, stopPolling} =
        useGetImport2ImportStatus(props.originId);

    const {importInitiate, initiateError} = useInitiateImport();
    const onInitiateImport = (crmType: CrmOptionType) => {
        importInitiate({
            optionType: crmType,
            isSupplemental: true,
        }).then((response) => {
            if (response && response.data && response.data.import2Initiate) {
                const originId = response.data.import2Initiate.originId;

                // Will always be a new report, so we're free to always fire an event
                analyticsClient.track(analyticsClient.EVENTS.TRACK_IMPORT2_CRM_TYPE_SELECTED, {
                    crmType,
                });

                // Redirect to the status page for the import
                if (originId) {
                    appHistory.push(`/setup/import-import2/status/${originId}`);
                }
            }
        });
    };

    return (
        <>
            {initiateError && (
                <FlashBanner type='error' isOverlay={true} duration={6000}>
                    Oops – something went wrong starting your supplemental import - please try again
                    and contact our support team if this error persists!
                </FlashBanner>
            )}
            {isLoading ? (
                <LoadingPage />
            ) : (
                <PageContainer
                    title='Import status'
                    controlBar={
                        importStatus && (
                            <div className='flex justify-end border-bottom background-white py-8 px-16'>
                                <StatusBar
                                    // @ts-expect-error
                                    status={getImportStatusBadge(importStatus.import2Status)}
                                    primaryText={`Import2 migration from ${importStatus.crmOption.label}`}
                                    secondaryText={
                                        <div className='flex justify-end width-fit-content'>
                                            Started&nbsp;
                                            <RelativeTimestamp datetime={importStatus.startTime} />
                                            &nbsp;by&nbsp;
                                            {importStatus.initiatingUser
                                                ? importStatus.initiatingUser.name
                                                : 'Unknown'}
                                        </div>
                                    }
                                />
                            </div>
                        )
                    }
                >
                    <div className={styles.content}>
                        <div className={styles['main-content']}>
                            {errorMessage ? <ErrorState errorMessage={errorMessage} /> : undefined}
                            {importStatus ? (
                                <ImportStatus
                                    importStatus={importStatus}
                                    refetchImportStatus={refetch}
                                    startPolling={startPolling}
                                    stopPolling={stopPolling}
                                    // @ts-expect-error
                                    originId={props.originId}
                                    onInitiateImport={onInitiateImport}
                                />
                            ) : undefined}
                        </div>
                        <SettingsSidebar glossaryKey='/setup/import-import2' />
                    </div>
                </PageContainer>
            )}
        </>
    );
}
